/**
 * ConfigSection - Data-driven node configuration UI
 *
 * Renders config fields declaratively based on NodeTypeSpec.config_fields.
 * Replaces the hardcoded node-type-specific logic in NodeConfigPanel.
 */

import React from 'react';
import type { NodeType, NodeData } from '@/types/imageWorkflow';
import type { InputFieldMapping, FieldType } from '@/types/workflowFields';
import { useNodeConfigFields, isConfigFieldVisible } from '../hooks/useNodeTypes';
import { ConfigFieldRenderer } from './ConfigFieldRenderer';
import { useFieldSourceMappings } from './FieldSourceToggle';

// ============================================================================
// Types
// ============================================================================

interface ConfigSectionProps {
  /** Node type for fetching config fields */
  nodeType: NodeType | string;
  /** Current node data */
  nodeData: NodeData;
  /** Callback to update node data */
  onUpdate: (updates: Partial<NodeData>) => void;
  /** Whether the node is currently executing (disable editing) */
  isDisabled?: boolean;
  /** Input field mappings (for __config__ prefixed keys) */
  inputMappings: InputFieldMapping[];
  /** Callback to update a mapping */
  updateMapping: (
    inputFieldKey: string,
    sourceNodeKey: string,
    sourceOutputKey: string,
    keyPath?: string
  ) => void;
  /** Callback to update key path */
  updateKeyPath: (inputFieldKey: string, keyPath: string | undefined) => void;
  /** Callback to remove a mapping */
  removeMapping: (inputFieldKey: string) => void;
  /** Available sources for mapping - matches FieldSourceToggle types */
  availableSources?: Array<{
    node_key: string;
    node_type: string;
    node_label: string;
    outputs: Array<{ key: string; type: FieldType; label: string | undefined }>;
  }>;
  /** Custom inputs from input fields section */
  customInputs?: Array<{
    key: string;
    type: FieldType;
    label?: string;
  }>;
  /** Context variables - matches FieldSourceToggle types */
  contextVariables?: Array<{
    key: string;
    label: string;
    type: FieldType;
  }>;
  /** Start variables - matches FieldSourceToggle types */
  startVariables?: Array<{
    key: string;
    value: string;
  }>;
}

// ============================================================================
// Component
// ============================================================================

export const ConfigSection: React.FC<ConfigSectionProps> = ({
  nodeType,
  nodeData,
  onUpdate,
  isDisabled = false,
  inputMappings,
  updateMapping,
  updateKeyPath,
  removeMapping,
  availableSources = [],
  customInputs = [],
  contextVariables = [],
  startVariables = [],
}) => {
  const { configFields, isLoading, error } = useNodeConfigFields(nodeType);

  // Create field source mapping handlers (for __config__ prefixed keys)
  const fieldSourceHandlers = useFieldSourceMappings(
    inputMappings,
    updateMapping,
    updateKeyPath,
    removeMapping
  );

  // Filter visible fields based on show_when conditions
  const visibleFields = configFields.filter((field) =>
    isConfigFieldVisible(field, nodeData as Record<string, unknown>)
  );

  if (isLoading) {
    return (
      <div className="p-4 text-center text-gray-500 text-sm">
        Loading configuration...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500 text-sm">
        Failed to load configuration
      </div>
    );
  }

  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
        <svg
          className="w-4 h-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span>Config Options</span>
      </div>

      <div className="space-y-3">
        {visibleFields.map((field) => (
          <ConfigFieldRenderer
            key={field.key}
            field={field}
            value={nodeData[field.key as keyof NodeData]}
            onChange={(value) => onUpdate({ [field.key]: value })}
            nodeData={nodeData}
            nodeType={nodeType as NodeType}
            isDisabled={isDisabled}
            fieldSourceHandlers={
              field.mapping_enabled
                ? fieldSourceHandlers.createFieldHandlers(field.key)
                : undefined
            }
            // Pass full handlers for custom renderers to create subfield handlers
            fieldSourceHandlersFactory={
              field.type === 'custom' ? fieldSourceHandlers : undefined
            }
            availableSources={availableSources}
            customInputs={customInputs}
            contextVariables={contextVariables}
            startVariables={startVariables}
            onBatchUpdate={onUpdate}
          />
        ))}
      </div>
    </div>
  );
};

export default ConfigSection;
